/**
 * FormDetail — the core of the Agent Form Workspace (trec-svc /trec/forms/{id}).
 *
 * This is where extracted document data becomes a signable contract:
 *   1. "Auto-fill from documents" → POST /prefill pulls the client's
 *      document-extraction values (+ MLS property row) into the form fields.
 *   2. Each field row shows its provenance (auto-extracted / agent edit /
 *      client input) and is editable while the form is DRAFT/PREFILLED;
 *      "Save edits" → PUT /fields marks touched fields source='agent_edit'.
 *   3. "Send for signature" → POST /send builds the DocuSign envelope, then the
 *      form locks (fields frozen once SENT_FOR_SIGNATURE).
 *
 * Edits are staged locally and flushed on Save so the agent can review the
 * whole prefilled set before committing — the backend never clobbers an
 * agent_edit on re-prefill, so the two buttons compose safely.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  formService,
  packetService,
  type FieldOut,
  type FormOut,
} from "@re-platform/api-client";
import { Button, Card, Skeleton, StatusPill, Toast } from "@re-platform/ui";

import { api } from "../../lib/api";
import {
  fieldLabel,
  formTypeLabel,
  isEditable,
  sourceLabel,
  sourceTone,
  statusLabel,
  statusTone,
} from "./formHelpers";

export function FormDetail() {
  const { formId = "" } = useParams<{ formId: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [toast, setToast] = useState<string | null>(null);
  // Staged edits keyed by field_name — only the ones the agent actually changed.
  const [edits, setEdits] = useState<Record<string, string>>({});

  const formQuery = useQuery({
    queryKey: ["form", formId],
    queryFn: () => formService.get(api, formId),
  });
  const form = formQuery.data;
  // A SENT form that rides on a packet stays editable: the client asked for a change,
  // the agent corrects it here, and "Revise & resend" replaces the live envelope. The
  // edits are STAGED — what the client is holding is untouched until the resend.
  const revisable = Boolean(
    form && form.packet_id && form.status === "SENT_FOR_SIGNATURE",
  );
  const editable = form ? isEditable(form.status) || revisable : false;

  // Clear staged edits whenever the server form changes (prefill/save/send).
  useEffect(() => {
    setEdits({});
  }, [form?.updated_at, form?.status]);

  function refresh(updated: FormOut) {
    queryClient.setQueryData(["form", formId], updated);
    void queryClient.invalidateQueries({ queryKey: ["forms"] });
  }

  const prefillMutation = useMutation({
    mutationFn: () => formService.prefill(api, formId),
    onSuccess: (f) => {
      refresh(f);
      setToast("Auto-filled from the client's documents.");
    },
    onError: (e) =>
      setToast(e instanceof Error ? e.message : "Auto-fill failed."),
  });

  const saveMutation = useMutation({
    mutationFn: (fields: { field_name: string; field_value: string }[]) =>
      formService.updateFields(api, formId, fields),
    onSuccess: (f) => {
      refresh(f);
      setToast("Edits saved.");
    },
    onError: (e) => setToast(e instanceof Error ? e.message : "Save failed."),
  });

  const sendMutation = useMutation({
    mutationFn: () => formService.send(api, formId),
    onSuccess: (f) => {
      refresh(f);
      setToast("Sent for signature.");
    },
    onError: (e) => setToast(e instanceof Error ? e.message : "Send failed."),
  });

  // Releasing a HELD packet (gap I25). The whole packet waited on this form, so
  // Send must go out as ONE envelope carrying every form in it — sending just
  // this one would strand the notices and give the client the second signing
  // ceremony the hold exists to prevent.
  const sendPacketMutation = useMutation({
    mutationFn: () => packetService.send(api, form!.packet_id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["form", formId] });
      void queryClient.invalidateQueries({ queryKey: ["forms"] });
      setToast("Sent — the client has all their documents in one envelope.");
    },
    onError: (e) => setToast(e instanceof Error ? e.message : "Send failed."),
  });

  const voidMutation = useMutation({
    mutationFn: () => formService.void(api, formId),
    onSuccess: (f) => {
      refresh(f);
      setToast("Withdrawn — the form is editable again.");
    },
    onError: (e) =>
      setToast(e instanceof Error ? e.message : "Withdraw failed."),
  });

  const resendMutation = useMutation({
    mutationFn: () => packetService.resend(api, form!.packet_id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["form", formId] });
      void queryClient.invalidateQueries({ queryKey: ["forms"] });
      setToast(
        "Resent — the old envelope is void and the client has the corrected agreement.",
      );
    },
    onError: (e) => setToast(e instanceof Error ? e.message : "Resend failed."),
  });

  const deleteMutation = useMutation({
    mutationFn: () => formService.remove(api, formId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["forms"] });
      navigate("/forms");
    },
    onError: (e) => setToast(e instanceof Error ? e.message : "Delete failed."),
  });

  const dirtyFields = useMemo(
    () =>
      Object.entries(edits).map(([field_name, field_value]) => ({
        field_name,
        field_value,
      })),
    [edits],
  );
  const busy =
    prefillMutation.isPending ||
    saveMutation.isPending ||
    sendMutation.isPending ||
    sendPacketMutation.isPending ||
    voidMutation.isPending ||
    deleteMutation.isPending;

  // This form belongs to a packet that has never been sent, so it is the held
  // one the rest of the application is waiting on: Send releases them together.
  // A form the agent created ad hoc has no packet and sends on its own.
  const releasesPacket = Boolean(
    form?.packet_id && !form?.docusign_envelope_id,
  );

  if (formQuery.isPending) {
    return (
      <div>
        <Skeleton variant="block" style={{ height: 80, marginBottom: 16 }} />
        <Skeleton variant="block" style={{ height: 320 }} />
      </div>
    );
  }
  if (formQuery.isError || !form) {
    return (
      <Card padding="lg">
        <p style={{ margin: 0 }}>
          Couldn't load this form
          {formQuery.error instanceof Error
            ? `: ${formQuery.error.message}`
            : "."}{" "}
          <Link to="/forms">Back to forms</Link>
        </p>
      </Card>
    );
  }

  const fields = form.fields;
  // What is actually holding the packet. Computed by the server against the
  // completeness gate's own predicate — never re-derived from field_value here,
  // because "NA" looks filled and a default-ticked election looks blank.
  const blocking = fields
    .filter((f) => f.blocks_send)
    // Same order as the groups below. Source order put "¶12.E · ¶5.G" in the
    // banner, which reads as a list nobody sorted.
    .sort((a, b) =>
      compareParagraphs(a.paragraph_label ?? "", b.paragraph_label ?? ""),
    );
  const groups = groupByParagraph(fields);

  return (
    <>
      <Link to="/forms" style={{ fontSize: 13 }}>
        ← Forms
      </Link>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginTop: 8,
        }}
      >
        <h1 style={{ margin: 0 }}>{formTypeLabel(form.form_type)}</h1>
        <StatusPill tone={statusTone(form.status)}>
          {statusLabel(form.status)}
        </StatusPill>
      </div>
      <p style={{ color: "var(--rp-color-text-muted)", marginTop: 4 }}>
        TREC template <code>{form.template_version}</code>
        {form.docusign_envelope_id && (
          <>
            {" · "}DocuSign envelope <code>{form.docusign_envelope_id}</code>
          </>
        )}
      </p>

      {/* action bar */}
      <Card padding="md" style={{ marginTop: 8 }}>
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Button
            variant="secondary"
            onClick={() => prefillMutation.mutate()}
            disabled={!editable || busy}
          >
            {prefillMutation.isPending
              ? "Auto-filling…"
              : "Auto-fill from documents"}
          </Button>
          <Button
            onClick={() => saveMutation.mutate(dirtyFields)}
            disabled={!editable || busy || dirtyFields.length === 0}
          >
            {saveMutation.isPending
              ? "Saving…"
              : dirtyFields.length > 0
                ? `Save ${dirtyFields.length} edit${dirtyFields.length === 1 ? "" : "s"}`
                : "Save edits"}
          </Button>
          {/* Delete is only possible while the form was never sent. */}
          {!form.docusign_envelope_id && (
            <Button
              variant="ghost"
              onClick={() => {
                if (
                  window.confirm(
                    "Delete this draft form? This can't be undone.",
                  )
                )
                  deleteMutation.mutate();
              }}
              disabled={busy}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete draft"}
            </Button>
          )}
          <div style={{ flex: 1 }} />
          {revisable && (
            <Button
              variant="secondary"
              onClick={() => {
                if (
                  window.confirm(
                    "Resend with your corrections? The envelope the client has now is voided and replaced.",
                  )
                )
                  resendMutation.mutate();
              }}
              disabled={busy || dirtyFields.length > 0}
              title={
                dirtyFields.length > 0
                  ? "Save your edits first — the resend sends what is saved."
                  : undefined
              }
            >
              {resendMutation.isPending ? "Resending…" : "Revise & resend"}
            </Button>
          )}
          {form.status === "SENT_FOR_SIGNATURE" && (
            <Button
              variant="secondary"
              onClick={() => {
                if (
                  window.confirm(
                    "Withdraw this form? The client's signing link stops working and the form reopens for edits.",
                  )
                )
                  voidMutation.mutate();
              }}
              disabled={busy}
            >
              {voidMutation.isPending ? "Withdrawing…" : "Withdraw"}
            </Button>
          )}
          <Button
            onClick={() =>
              releasesPacket
                ? sendPacketMutation.mutate()
                : sendMutation.mutate()
            }
            disabled={!editable || busy || fields.length === 0}
            title={
              releasesPacket
                ? "Sends every document in this application as one envelope."
                : undefined
            }
          >
            {sendMutation.isPending || sendPacketMutation.isPending
              ? "Sending…"
              : releasesPacket
                ? "Send all documents for signature"
                : "Send for signature"}
          </Button>
        </div>
        {!editable && (
          <p
            style={{
              margin: "10px 0 0",
              fontSize: 13,
              color: "var(--rp-color-text-muted)",
            }}
          >
            This form is {statusLabel(form.status).toLowerCase()} — fields are
            frozen.
          </p>
        )}
      </Card>

      {/* client handoff — appears once the form has been sent */}
      {(form.status === "SENT_FOR_SIGNATURE" || form.status === "SIGNED") && (
        <SendToClientCard formId={form.id} signed={form.status === "SIGNED"} />
      )}

      {/* fields */}
      <h2 style={{ fontSize: 16, marginBottom: 4 }}>Fields</h2>
      {blocking.length > 0 && (
        <Card
          padding="md"
          style={{
            marginBottom: 12,
            borderLeft: "3px solid var(--rp-color-danger)",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {blocking.length} field{blocking.length === 1 ? "" : "s"} must be
            completed before this can be sent
          </div>
          {/* The paragraph numbers, because that is what is printed on the page
              in front of the agent — and the same identifiers the send's own
              refusal uses, so the two describe the gap the same way. */}
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: "var(--rp-color-text-muted)",
            }}
          >
            {blocking
              .map((f) =>
                f.paragraph_label
                  ? `¶${f.paragraph_label}`
                  : fieldLabel(f.field_name),
              )
              .join(" · ")}
          </p>
        </Card>
      )}
      {fields.length === 0 ? (
        <Card padding="lg">
          <p style={{ margin: 0 }}>
            No fields yet. Click <strong>Auto-fill from documents</strong> to
            pull values from the client's uploaded documents.
          </p>
        </Card>
      ) : (
        groups.map((group) => (
          <Card
            key={group.key}
            padding="none"
            style={{ overflow: "hidden", marginBottom: 12 }}
          >
            {group.heading && (
              <div
                style={{
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  background: "var(--rp-color-surface-muted)",
                  borderBottom: "1px solid var(--rp-color-border)",
                }}
              >
                {group.heading}
              </div>
            )}
            {group.fields.map((field, i) => (
              <FieldRow
                key={field.field_name}
                field={field}
                first={i === 0 && !group.heading}
                editable={editable}
                draft={edits[field.field_name]}
                onChange={(value) =>
                  setEdits((prev) => ({ ...prev, [field.field_name]: value }))
                }
              />
            ))}
          </Card>
        ))
      )}

      <Toast message={toast} onDismiss={() => setToast(null)} />
    </>
  );
}

/**
 * Client handoff. On send, the backend emails the client a signing link
 * (trec.form.sent → notification-svc); this card surfaces the same link so the
 * agent can also share it directly (text, chat) or re-send. The web app lives
 * on its own origin (app.<root>), so the base comes from VITE_WEB_APP_BASE_URL;
 * we fall back to the current origin for local dev where both run on vite.
 */
function SendToClientCard({
  formId,
  signed,
}: {
  formId: string;
  signed: boolean;
}) {
  const webBase = (
    (import.meta.env.VITE_WEB_APP_BASE_URL as string | undefined) ??
    window.location.origin
  ).replace(/\/$/, "");
  const link = `${webBase}/trec/sign/${formId}`;
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Card padding="md" style={{ marginTop: 12 }}>
      <div
        className="rp-card__title"
        style={{ fontWeight: 600, marginBottom: 6 }}
      >
        {signed ? "Signing link" : "Send to client"}
      </div>
      <p
        style={{
          margin: "0 0 10px",
          fontSize: 13,
          color: "var(--rp-color-text-muted)",
        }}
      >
        {signed
          ? "This form is signed. The link below stays valid for the client to view."
          : "The client has been emailed a secure link to review and sign. You can also share this link directly:"}
      </p>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <input
          readOnly
          value={link}
          onFocus={(e) => e.currentTarget.select()}
          style={{
            flex: 1,
            minWidth: 240,
            padding: "6px 8px",
            font: "inherit",
            fontSize: 13,
            border: "1px solid var(--rp-color-border)",
            borderRadius: "var(--rp-radius-sm)",
            background: "var(--rp-color-surface-muted)",
          }}
        />
        <Button variant="secondary" size="sm" onClick={copy}>
          {copied ? "Copied!" : "Copy link"}
        </Button>
      </div>
    </Card>
  );
}

/**
 * One card per paragraph of the printed form, in the order the paragraphs
 * appear on it.
 *
 * 🔴 WHY THIS EXISTS. The workspace rendered `form.fields` as one flat list of
 * identical text inputs. That was fine at 36 rows; V093 took a lease listing
 * agreement to 107, and an agent looking for the box that is holding their
 * packet had a hundred rows named things like `elect_mls_file_prompt` to read
 * through. The completeness gate refuses the send by PARAGRAPH — "¶5.A(1)(a)" —
 * so the screen has to speak in paragraphs too, or the refusal names a place
 * the agent cannot find.
 *
 * Sorting is NUMERIC-AWARE per component: a plain string sort puts "¶12.E"
 * before "¶2.B" and "¶20.C" before "¶5.A", which on a listing agreement scatters
 * the fee paragraph into the middle of the lease requirements. Fields the
 * template does not map (or a form with no template at all) carry no label and
 * collect in one trailing group, exactly as they render today.
 */
function groupByParagraph(
  fields: FieldOut[],
): { key: string; heading: string | null; fields: FieldOut[] }[] {
  const byLabel = new Map<string, FieldOut[]>();
  for (const field of fields) {
    const key = field.paragraph_label ?? "";
    const bucket = byLabel.get(key);
    if (bucket) bucket.push(field);
    else byLabel.set(key, [field]);
  }
  return [...byLabel.entries()]
    .sort(([a], [b]) => {
      // The unlabelled group always sits last — it is the leftovers, not ¶0.
      if (a === "") return 1;
      if (b === "") return -1;
      return compareParagraphs(a, b);
    })
    .map(([label, group]) => ({
      key: label || "__unlabelled",
      heading: label
        ? `¶${label}`
        : fields.some((f) => f.paragraph_label)
          ? "Other fields"
          : null,
      fields: group,
    }));
}

/** "5.A(1)(a)" vs "12.E" — compare component by component, numbers as numbers. */
function compareParagraphs(a: string, b: string): number {
  const parts = (s: string) => s.split(/[.()]+/).filter(Boolean);
  const left = parts(a);
  const right = parts(b);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const l = left[i];
    const r = right[i];
    if (l === undefined) return -1;
    if (r === undefined) return 1;
    const ln = Number(l);
    const rn = Number(r);
    // Both numeric → compare as numbers (5 before 12). Otherwise as text.
    const diff =
      Number.isNaN(ln) || Number.isNaN(rn) ? l.localeCompare(r) : ln - rn;
    if (diff !== 0) return diff;
  }
  return 0;
}

function FieldRow({
  field,
  first,
  editable,
  draft,
  onChange,
}: {
  field: FieldOut;
  first: boolean;
  editable: boolean;
  /** Staged edit value, or undefined when untouched. */
  draft: string | undefined;
  onChange: (value: string) => void;
}) {
  const value = draft ?? field.field_value ?? "";
  // 🔴 What the tab will actually carry. An election is ticked by the template's
  // default and never stored, so `value` says "not ticked" about a box that
  // prints ticked — the agent would be confirming a different form from the one
  // the client receives.
  const effective = draft ?? field.effective_value ?? field.field_value ?? "";
  const dirty = draft !== undefined && draft !== (field.field_value ?? "");
  // A tick is a tick. Rendering a checkbox as a text input meant the agent had
  // to know that "X" ticks the box and anything else does not — on a lease
  // listing agreement that is 33 boxes to type into by hand.
  const isCheckbox = field.field_kind === "checkbox";
  // 🔴 Straight from the server. Re-deriving it from `value` here would mark
  // "NA" as filled (it is not — the gate refuses it) and mark every
  // default-ticked election as blocking (they are not).
  const blocking = field.blocks_send === true && !dirty;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(180px, 1fr) 2fr auto",
        gap: 12,
        alignItems: "center",
        padding: "12px 16px",
        borderTop: first ? undefined : "1px solid var(--rp-color-border)",
        borderLeft: blocking ? "3px solid var(--rp-color-danger)" : undefined,
        background: dirty ? "var(--rp-color-warning-soft)" : undefined,
      }}
    >
      <label
        htmlFor={`field-${field.field_name}`}
        style={{ fontSize: 14, fontWeight: 500 }}
      >
        {fieldLabel(field.field_name)}
        {field.is_required && (
          <span
            aria-label="required"
            title="Cannot be blank when this is sent"
            style={{ color: "var(--rp-color-danger)", marginLeft: 4 }}
          >
            *
          </span>
        )}
      </label>
      {isCheckbox ? (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
          }}
        >
          <input
            id={`field-${field.field_name}`}
            type="checkbox"
            // Anything truthy other than the form's own "false" ticks the box —
            // the same rule the send path applies when it builds the tab, so
            // what the agent sees here is what prints.
            checked={effective !== "" && effective !== "false"}
            disabled={!editable}
            onChange={(e) => onChange(e.target.checked ? "X" : "")}
          />
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            {effective !== "" && effective !== "false"
              ? "Ticked"
              : "Not ticked"}
          </span>
        </label>
      ) : (
        <input
          id={`field-${field.field_name}`}
          value={value}
          disabled={!editable}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            blocking
              ? "Required — cannot be sent blank"
              : (field.effective_value ?? "—")
          }
          style={{
            padding: "6px 8px",
            font: "inherit",
            fontSize: 14,
            border: `1px solid ${
              blocking ? "var(--rp-color-danger)" : "var(--rp-color-border)"
            }`,
            borderRadius: "var(--rp-radius-sm)",
            width: "100%",
            background: editable ? undefined : "var(--rp-color-surface-muted)",
          }}
        />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <StatusPill tone={sourceTone(field.source)}>
          {sourceLabel(field.source)}
        </StatusPill>
        {field.source_document_id && (
          <span
            title={
              field.source_document_filename
                ? `From ${field.source_document_filename}`
                : `Source document ${field.source_document_id}`
            }
            style={{
              fontSize: 11,
              color: "var(--rp-color-text-muted)",
              maxWidth: 160,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {field.source_document_filename ??
              `doc ${field.source_document_id.slice(0, 8)}`}
          </span>
        )}
      </div>
    </div>
  );
}
